<script setup lang="ts">
import { toRefs } from 'vue'
import type { ComboBoxStore } from './store'
import type { Result } from './results'

const props = defineProps<{
  store: ComboBoxStore
  placeholder: string
  readonly: boolean
  isStatic?: boolean
}>()

const { query, isDropdownOpen, hasQuery, unselectedResults, isLoading } = storeToRefs(props.store)
const { search, selectResult, closeDropdown, toggleDropdown } = toRefs(props.store)

const newResult = computed<Result>(() => props.store.textToResult(query.value))
const showCreateNew = computed(() => hasQuery.value as boolean && !props.readonly)
</script>

<template>
  <div class="relative">
    <div class="relative">
      <input
        ref="queryInput"
        v-model="query"
        type="text"
        class="w-full rounded-lg border border-gray-300 text-black placeholder-gray-300 drop-shadow-xs"
        :placeholder="placeholder"
        @input="search(true)"
        @focus="search(isStatic)"
        @keyup.esc="closeDropdown"
      >
      <i
        v-if="isStatic"
        :class="{'fa-chevron-up': isDropdownOpen, 'fa-chevron-down': !isDropdownOpen}"
        class="fa-regular absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-700"
        @click="isDropdownOpen ? toggleDropdown() : search(true)"
      />
      <i
        v-else
        class="fa-regular fa-magnifying-glass absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-700"
      />
    </div>
    <div
      v-if="isDropdownOpen"
      class="absolute z-50 mt-1 w-full drop-shadow-xs"
    >
      <div class="max-h-40 overflow-auto rounded-lg border bg-white">
        <div
          v-if="showCreateNew"
          class="hover:bg-primary-50 cursor-pointer py-1 pl-3"
          @click="selectResult(newResult)"
        >
          <span class="text-sm font-medium text-gray-700">
            <i class="fa-regular fa-plus mr-1" /> Create {{ newResult.text1 }}
          </span>
        </div>

        <div
          v-if="isLoading"
          class="flex justify-center p-2"
        >
          <SpinnerV2 />
        </div>

        <div v-if="!isLoading && unselectedResults.length > 0">
          <ResultV2
            v-for="result in unselectedResults"
            :key="result.id"
            :store="store"
            :result="result"
          />
        </div>
        <div
          v-else-if="!isLoading && unselectedResults.length == 0"
          class="inline-block py-1 pl-3 text-sm font-medium text-gray-700"
        >
          No results found.
        </div>
      </div>
    </div>
  </div>
</template>
